import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import './ChatDialog.scss';
import { Box, Button, DialogTitle, List, ListItem, ListItemText, Paper, TextField } from '@mui/material';
import Dialog from './ActionDialog';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlayerAvatar from './PlayerAvatar';
import ExpandableText from './ExpandableText';

export default function ChatDialog({ open, onClose, chatInfos, playerInfos, playerId, roomId, message, typeMessage, onSendClick }) {
    const { t } = useTranslation();
    const chatContainerRef = useRef(null);
    const [lastMessageId, setLastMessageId] = useState(null);

    useEffect(() => {
        if (chatInfos && chatInfos.length > 0) {
            const newLastMsgId = chatInfos[chatInfos.length - 1]?.chatId;
            if (lastMessageId !== newLastMsgId) {
                scrollChatBottom();
                setLastMessageId(newLastMsgId);
            }
        }
    }, [chatInfos]);

    useEffect(() => {
        if(open){
            scrollChatBottom();
        }
    }, [open])

    const scrollToTop = () => {
        const scrollY = document.body.style.top;
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }


    const scrollChatBottom = () => {
        setTimeout(() => {
            if (chatContainerRef.current) {
                requestAnimationFrame(() => {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                });
            }
        }, 0);
    }
    return (
        <Dialog
            role="dialog"
            aria-labelledby="chat-dialog-title"
            aria-modal="true"
            onClose={onClose}
            fullWidth={true}
            open={open}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: 'unset',
                    margin: '0.938rem',
                    height: '80vh',
                    '@supports (height: 100dvh)': { height: '80dvh' },
                    display: 'flex',
                    flexDirection: 'column',
                    width: 'calc(100% - 1.563rem)'
                },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, textAlign: 'center', borderBottom: '0.063rem solid #bbbbbb' }} id="chat-dialog-title">
                {t('chat.title')}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box
                ref={chatContainerRef}
                sx={{
                    flex: 1, // Fills the available height
                    minHeight: 0,
                    overflowY: 'auto', // Enables vertical scrolling
                    padding: 2,
                }}
            >
                {chatInfos
                    ?.map((chatInfo, index) => {
                        const player = playerInfos?.find((p) => p?.playerId === chatInfo?.playerId);
                        const playerName = player ? player?.playerName : t('chat.leftPlayer');
                        const mine = chatInfo.playerId === playerId;
                        return (
                            <Paper key={chatInfo.chatId} className={`chat-bubble ${mine ? 'chat-bubble--mine' : 'chat-bubble--other'}`} elevation={3}>
                                <List sx={{ padding: '0' }}>
                                    <ListItem key={index} sx={{
                                        paddingTop: '0',
                                        paddingBottom: '0'
                                    }}>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    sx={{ color: 'primary.main', fontWeight: 'bold', wordWrap: 'break-word' }}
                                                    variant="body1"
                                                >
                                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PlayerAvatar roomId={roomId} playerId={chatInfo.playerId} name={playerName} sx={{ width: 28, height: 28, fontSize: '.8rem' }} />
                                                        <ExpandableText text={`${mine ? `${t('chat.you')} (${playerName})` : playerName}:`} />
                                                    </Box>
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    sx={{ color: 'text.secondary', fontWeight: 'light', wordWrap: 'break-word' }}
                                                    variant="body2"
                                                >
                                                    <ExpandableText text={chatInfo?.message} />
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </List>
                            </Paper>
                        );
                    })}
            </Box>
            <Box
                component="form"
                sx={{
                    padding: 1,
                    display: 'flex',
                    gap: 1, // Adds space between the input and button
                    alignItems: 'center',
                    borderTop: '0.063rem solid #bbbbbb'
                }}
                onSubmit={(e) => {
                    e.preventDefault(); // Prevent the default form submission
                    onSendClick(); // Trigger the send action
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    autoComplete='off'
                    placeholder={t('chat.placeholder')}
                    value={message}
                    onChange={(e) => typeMessage(e.target.value)}
                    onBlur={scrollToTop}
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
                            padding: '0.781rem 0.875rem'
                        }
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                >
                    {t('common.send')}
                </Button>
            </Box>
        </Dialog>
    );
}
